import React from 'react';
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

export interface Cluster {
  id: string;
  name: string;
  status: string;
  cloud: string;
  region: string;
  description: string;
  reflectors: string[];
  lastSync: string;
  clusterID: string;
  version: string;
  tag: string;
  lastSyncAt: string;
  instances: string[];
  permissions: string[];
  vpcId: string;
  createdAt: string;
  updatedAt: string;
}

const ClusterIdCell = ({ row }: { row: Cluster }) => {
  const navigate = useNavigate();
  return (
    <button
      className="text-semantic-text-brand underline font-medium hover:text-blue-700"
      onClick={() => navigate(`/reflector-v2/management/clusters/details/${row.id}`)}
    >
      {row.clusterID}
    </button>
  );
};

export const clusterColumns: GenericTableColumn<Cluster>[] = [
  {
    header: "Cluster ID",
    accessor: "id",
    renderCell: (row) => <ClusterIdCell row={row} />,
  },
  {
    header: "Name",
    accessor: "name",
  },
  {
    header: "Status",
    accessor: "status",
    renderCell: (row) => {
      let variant: "success" | "warning" | "error" | "default" = "default";
      
      if (row.status === "Online") {
        variant = "success";
      } else if (row.status === "Degraded") {
        variant = "warning";
      } else if (row.status === "Offline") {
        variant = "error";
      }
      
      return (
        <GenericTag
          label={row.status}
          variant={variant}
          size="small"
        />
      );
    },
  },
  {
    header: "Cloud",
    accessor: "cloud",
    renderCell: (row) => (
      <GenericTag
        label={row.cloud}
        variant={row.cloud === "AWS" ? "warning" : row.cloud === "GCP" ? "success" : "neutral"}
        size="small"
      />
    ),
  },
  {
    header: "Region",
    accessor: "region",
    renderCell: (row) => (
      <GenericTag
        label={row.region}
        variant="neutral"
        size="small"
      />
    ),
  },
  {
    header: "Description",
    accessor: "description",
  },
  {
    header: "Reflectors",
    accessor: "reflectors",
    renderCell: (row) => (
      <div className="flex flex-col gap-y-0.5">
        {Array.isArray(row.reflectors) && row.reflectors.length > 0
          ? row.reflectors.map((r) => (
              <GenericTag
                key={r}
                label={r}
                variant="custom"
                backgroundColor="bg-purple-100"
                textColor="text-purple-700"
                size="small"
                rounded="medium"
                className='text-2xs'
              />
            ))
          : <span className="text-gray-400">No reflectors</span>}
      </div>
    ),
  },
  {
    header: "Last Sync",
    accessor: "lastSync",
    renderCell: (row) => formatDateDMY(row.lastSync),
  },
  
];

function formatDateDMY(dateString: string) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  return format(date, "dd/MM/yyyy");
}

export const TABS = ["Device View", "Cluster View"];

// Cluster details section mock data
export const clusterDetailsSection = {
  header: 'Details',
  items: [
    { label: 'ID', value: 'CL-001' },
    { label: 'Name', value: 'Production HA Cluster', type: 'editable' },
    { label: 'Description', value: 'Production HA Cluster', type: 'editable' },
    { label: 'Status', value: 'Active', type: 'tag', variant: 'success' },
    { label: 'Cloud', value: 'AWS', type: 'tag', variant: 'warning' },
    { label: 'Region', value: 'us-east-1', type: 'tag', variant: 'neutral' },
    { label: 'VPC/VNET/VCN', value: 'vpc-12345' },
    { label: 'Reflectors', value: '2' },
    { label: 'Last Sync', value: '5/1/2023, 2:28:45 AM' },
    { label: 'Created', value: '2023-04-15 14:30:22' },
    { label: 'IAM Permissions', value: 'Degraded', type: 'iam', variant: 'warning' },
  ],
};



// Tabs for Cluster Details Page
export const clusterDetailsTabs = [
  { label: 'Details', value: 'Details' },
  { label: 'Reflectors', value: 'Reflectors' },
  { label: 'BGP', value: 'BGP' },
  { label: 'Cloud Propagation', value: 'Cloud Propagation' },
];
